import { useTypedAppFormContext } from "@/core/form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionRow } from "./ActionRow";
import { AddActionMenu } from "./AddActionMenu";
import { isConfigurable } from "./actionRegistry";
import { evalActionsFormOptions } from "./evalActionsForm.options";

/**
 * The "If not fulfilled" editor, shared by the limit and test-case modals. Reads
 * whichever form is providing context — both carry `actions`.
 *
 * Every action type renders through the same row, so the list is uniform no
 * matter how many types exist; see actionRegistry.ts for how a type is added.
 */
export function EvalActionsSection() {
  const { t } = useTranslation(["evaluation", "common"]);
  const form = useTypedAppFormContext({ ...evalActionsFormOptions });
  // One row open at a time keeps a long list readable. Indices shift on removal,
  // so this collapses rather than trying to track the moved row.
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-col overflow-hidden rounded-lg border border-border">
        <form.AppField name="actions">
          {(field) => {
            const actions = field.state.value;

            return (
              <>
                <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-3 py-2">
                  <span className="text-xs font-semibold text-muted-foreground">
                    {t("actions.ifNotFulfilled")}
                  </span>
                  <div className="ml-auto">
                    <AddActionMenu
                      actions={actions}
                      onAdd={(next, addedIndex) => {
                        field.handleChange(next);
                        // Open the new row only when there is something to set,
                        // so adding an Alert stays a single click.
                        setExpandedIndex(
                          isConfigurable(next[addedIndex]!.type)
                            ? addedIndex
                            : null,
                        );
                      }}
                    />
                  </div>
                </div>

                {actions.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-muted-foreground">
                    {t("actions.empty")}
                  </p>
                ) : (
                  <div className="divide-y divide-border">
                    {actions.map((action, index) => (
                      <ActionRow
                        key={`${action.type}-${index}`}
                        index={index}
                        actions={actions}
                        expanded={expandedIndex === index}
                        onToggle={() =>
                          setExpandedIndex(
                            expandedIndex === index ? null : index,
                          )
                        }
                        onRemove={() => {
                          field.handleChange(
                            actions.filter((_, i) => i !== index),
                          );
                          setExpandedIndex(null);
                        }}
                      />
                    ))}
                  </div>
                )}
              </>
            );
          }}
        </form.AppField>
      </div>

      <form.Subscribe selector={(state) => state.fieldMeta.actions?.errors}>
        {(errors) =>
          errors?.length ? (
            <p className="text-xs text-destructive">
              {errors.map((e) => e?.message ?? String(e)).join(", ")}
            </p>
          ) : null
        }
      </form.Subscribe>
    </section>
  );
}
